import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  List,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import {
  ApiError,
  createReservation,
  fetchServiceConstraints,
  type CustomerSummary,
  type ServiceConstraints,
  type Zone,
} from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';
import { estimateReservationPrice, formatZonePricing } from '../ui/formatters.ts';
import { CustomerPicker } from './CustomerPicker.tsx';

interface Props {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly onCreated: (reservationId: string) => void;
  readonly venueId: string;
  readonly timezone: string;
  readonly zones: readonly Zone[];
}

function combineToIsoUtc(date: Date, timeHHMM: string, tz: string): string {
  const [h, m] = timeHHMM.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) {
    throw new Error('invalid time');
  }
  return dayjs
    .tz(`${dayjs(date).format('YYYY-MM-DD')} ${timeHHMM}:00`, tz)
    .utc()
    .toISOString();
}

/**
 * Prefills the modal with a sensible future slot: round 'now' up to the next
 * whole hour, end = start + durationMin (fallback 60). Avoids the footgun where
 * the date defaults to today + time defaults to blank, letting a user submit a
 * past time without noticing.
 */
function nextHourSlot(tz: string, durationMin = 60): { date: Date; start: string; end: string } {
  const now = dayjs().tz(tz);
  const start = now.minute() === 0 && now.second() === 0 && now.millisecond() === 0
    ? now.add(1, 'hour')
    : now.add(1, 'hour').minute(0).second(0).millisecond(0);
  const end = start.add(durationMin, 'minute');
  return {
    date: start.toDate(),
    start: start.format('HH:mm'),
    end: end.format('HH:mm'),
  };
}

function minutesBetween(startIso: string, endIso: string): number {
  return Math.round((Date.parse(endIso) - Date.parse(startIso)) / 60_000);
}

function validateLocally(
  c: ServiceConstraints,
  partySize: number,
  startIso: string,
  endIso: string,
  nowMs: number,
  timezone: string,
): string | null {
  if (!c.active) return '該服務目前停用，無法建立訂位';
  if (partySize < c.minPartySize || partySize > c.maxPartySize) {
    return `人數超出允許範圍（${c.minPartySize}–${c.maxPartySize}）`;
  }
  const startMs = Date.parse(startIso);
  const endMs = Date.parse(endIso);
  if (!(startMs < endMs)) return '結束時間必須晚於開始時間';
  const duration = minutesBetween(startIso, endIso);
  if (duration < c.durationMinMinutes || duration > c.durationMaxMinutes) {
    return `場次時長超出允許範圍（${c.durationMinMinutes}–${c.durationMaxMinutes} 分鐘，目前 ${duration} 分鐘）`;
  }
  if (startMs <= nowMs) {
    const nowLocal = dayjs(nowMs).tz(timezone).format('MM/DD HH:mm');
    const startLocal = dayjs(startMs).tz(timezone).format('MM/DD HH:mm');
    return `起始時間 ${startLocal} 已過（現在 ${nowLocal}），請選擇未來時段`;
  }
  if (startMs < nowMs + c.leadTimeMinutes * 60_000) {
    return `需至少提前 ${c.leadTimeMinutes} 分鐘訂位`;
  }
  for (const b of c.blackoutWindows) {
    const bs = Date.parse(b.start);
    const be = Date.parse(b.end);
    if (startMs < be && endMs > bs) {
      return `該時段與禁用時段衝突（${b.start} ~ ${b.end}）`;
    }
  }
  return null;
}

export function NewReservationModal(props: Props) {
  const { opened, onClose, onCreated, venueId, timezone, zones } = props;
  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const initialSlot = useMemo(() => nextHourSlot(timezone), [timezone]);
  const [date, setDate] = useState<Date | null>(initialSlot.date);
  const [startTime, setStartTime] = useState(initialSlot.start);
  const [endTime, setEndTime] = useState(initialSlot.end);
  const [partySize, setPartySize] = useState<number | ''>('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSummary | null>(null);
  const [customerRef, setCustomerRef] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [constraints, setConstraints] = useState<ServiceConstraints | null>(null);
  const [constraintsError, setConstraintsError] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const selectedZone = useMemo(
    () => zones.find((z) => z.resourceId === selectedZoneId) ?? zones[0] ?? null,
    [zones, selectedZoneId],
  );

  useEffect(() => {
    if (!opened) return;
    if (zones.length > 0 && !selectedZoneId) {
      setSelectedZoneId(zones[0]!.resourceId);
    }
    // Re-anchor prefilled slot on every open — a modal left open since yesterday
    // would otherwise carry stale defaults.
    const s = nextHourSlot(timezone, constraints?.durationMinMinutes ?? 60);
    setDate(s.date);
    setStartTime(s.start);
    setEndTime(s.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  useEffect(() => {
    if (!opened || !selectedZone) return;
    setConstraintsError(null);
    setConstraints(null);
    void fetchServiceConstraints(venueId, selectedZone.serviceId)
      .then(setConstraints)
      .catch((e) => {
        setConstraintsError(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
      });
  }, [opened, venueId, selectedZone]);

  useEffect(() => {
    if (!constraints || typeof partySize !== 'number') return;
    if (partySize < constraints.minPartySize || partySize > constraints.maxPartySize) {
      setPartySize('');
    }
  }, [constraints, partySize]);

  function reset() {
    const s = nextHourSlot(timezone, constraints?.durationMinMinutes ?? 60);
    setDate(s.date);
    setStartTime(s.start);
    setEndTime(s.end);
    setPartySize('');
    setSelectedCustomer(null);
    setCustomerRef('');
    setNotes('');
    setLocalError(null);
    setSubmitting(false);
  }

  async function handleSubmit() {
    setLocalError(null);
    if (!selectedZone) {
      setLocalError('請選擇區域');
      return;
    }
    if (!date || !startTime || !endTime || typeof partySize !== 'number') {
      setLocalError('請填寫日期、開始、結束時間與人數');
      return;
    }
    let startIso: string;
    let endIso: string;
    try {
      startIso = combineToIsoUtc(date, startTime, timezone);
      endIso = combineToIsoUtc(date, endTime, timezone);
    } catch {
      setLocalError('時間格式錯誤');
      return;
    }
    if (constraints) {
      const err = validateLocally(constraints, partySize, startIso, endIso, Date.now(), timezone);
      if (err) {
        setLocalError(err);
        return;
      }
    }
    setSubmitting(true);
    try {
      const result = await createReservation({
        venueId,
        serviceId: selectedZone.serviceId,
        scenarioType: selectedZone.scenarioType,
        partySize,
        startTime: startIso,
        endTime: endIso,
        source: 'operations-console',
        customerRef: customerRef.trim() === '' ? null : customerRef.trim(),
        ...(selectedCustomer ? { customerId: selectedCustomer.customerId } : {}),
        notes: notes.trim() === '' ? null : notes.trim(),
      });
      notifications.show({
        color: 'green',
        title: '訂位已建立',
        message: `建立成功，狀態：${result.status}`,
      });
      onCreated(result.reservationId);
      reset();
      onClose();
    } catch (e) {
      if (e instanceof ApiError) {
        const detail =
          typeof e.details === 'object' && e.details !== null
            ? ` · ${JSON.stringify(e.details)}`
            : '';
        setLocalError(`建立失敗：${e.message}${detail}`);
      } else {
        setLocalError(`建立失敗：${String(e)}`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="新增訂位" size="md" centered>
      <Stack gap="sm">
        {zones.length === 0 && (
          <Alert color="red" variant="light">
            尚無可用區域，請先完成 seed。
          </Alert>
        )}
        {zones.length > 0 && selectedZone && (
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              選擇區域
            </Text>
            <SegmentedControl
              value={selectedZone.resourceId}
              onChange={setSelectedZoneId}
              data={zones.map((z) => ({
                value: z.resourceId,
                label: z.zoneLabel,
              }))}
              color={tokens.color.mantine['primary']}
              fullWidth
            />
            <Group gap="xs">
              <Badge color={tokens.color.mantine['primary']} variant="filled" radius="sm">
                {formatZonePricing(selectedZone)}
              </Badge>
              <Badge color={tokens.color.mantine['primary']} variant="outline" radius="sm">
                每格 {selectedZone.unitCapacityMin === selectedZone.unitCapacityMax
                  ? `${selectedZone.unitCapacityMax} 人`
                  : `${selectedZone.unitCapacityMin}–${selectedZone.unitCapacityMax} 人`}
              </Badge>
              <Badge color={tokens.color.mantine['primary']} variant="outline" radius="sm">
                單場 {selectedZone.minPartySize}–{selectedZone.maxPartySize} 人
              </Badge>
            </Group>
          </Stack>
        )}
        {constraintsError && (
          <Alert color="red" variant="light" title="無法取得規則">
            {constraintsError}
          </Alert>
        )}
        {constraints && <ConstraintsPanel c={constraints} />}

        <DatePickerInput
          label="日期"
          placeholder="選擇日期"
          value={date}
          onChange={setDate}
          required
        />
        <Group grow>
          <TimeInput
            label="開始時間"
            value={startTime}
            onChange={(e) => setStartTime(e.currentTarget.value)}
            required
          />
          <TimeInput
            label="結束時間"
            value={endTime}
            onChange={(e) => setEndTime(e.currentTarget.value)}
            required
          />
        </Group>
        <NumberInput
          label="人數"
          placeholder={
            constraints ? `${constraints.minPartySize}–${constraints.maxPartySize}` : '例：4'
          }
          min={constraints?.minPartySize ?? 1}
          {...(constraints?.maxPartySize !== undefined ? { max: constraints.maxPartySize } : {})}
          value={partySize}
          onChange={(v) => setPartySize(typeof v === 'number' ? v : '')}
          required
        />
        {selectedZone && typeof partySize === 'number' && (
          <PriceEstimate
            zone={selectedZone}
            partySize={partySize}
            startTime={startTime}
            endTime={endTime}
          />
        )}
        <CustomerPicker
          venueId={venueId}
          selected={selectedCustomer}
          onSelect={setSelectedCustomer}
        />
        {!selectedCustomer && (
          <TextInput
            label="或直接填客人姓名 / 聯絡方式"
            placeholder="選填（walk-in 用）"
            value={customerRef}
            onChange={(e) => setCustomerRef(e.currentTarget.value)}
          />
        )}
        <Textarea
          label="備註"
          placeholder="選填"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={2}
        />
        {localError && (
          <Alert color="red" variant="light">
            {localError}
          </Alert>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="light" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            loading={submitting}
            disabled={!selectedZone || (constraints === null && constraintsError === null)}
          >
            建立訂位
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function PriceEstimate({
  zone,
  partySize,
  startTime,
  endTime,
}: {
  readonly zone: Zone;
  readonly partySize: number;
  readonly startTime: string;
  readonly endTime: string;
}) {
  const durationMinutes = computeMinutes(startTime, endTime);
  if (durationMinutes === null || durationMinutes <= 0) return null;
  const est = estimateReservationPrice({
    pricing: zone.pricing,
    partySize,
    durationMinutes,
    resourceCapacity: zone.totalUnits,
  });
  if (!est) {
    if (!zone.pricing) return null;
    return (
      <Alert color="yellow" variant="light">
        此人數 / 時段無對應計價（可能需要店家人工確認）
      </Alert>
    );
  }
  const perPerson = partySize > 0 ? Math.round(est.amount / partySize) : 0;
  return (
    <Alert color={tokens.color.mantine.primary} variant="light">
      <Group justify="space-between" align="baseline">
        <Stack gap={0}>
          <Text size="xs" c="dimmed">
            試算金額
          </Text>
          <Text fw={700} size="lg">
            {est.currency} {est.amount.toLocaleString()}
          </Text>
        </Stack>
        <Stack gap={0} align="flex-end">
          <Text size="xs" c="dimmed">
            每人平均
          </Text>
          <Text fw={600} size="sm">
            {est.currency} {perPerson.toLocaleString()}
          </Text>
        </Stack>
      </Group>
    </Alert>
  );
}

function computeMinutes(startHHMM: string, endHHMM: string): number | null {
  const parse = (s: string): number | null => {
    const [h, m] = s.split(':').map((n) => parseInt(n, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };
  const s = parse(startHHMM);
  const e = parse(endHHMM);
  if (s === null || e === null) return null;
  return e - s;
}

function ConstraintsPanel({ c }: { readonly c: ServiceConstraints }) {
  return (
    <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />} title="訂位規則">
      <List size="sm" spacing={2}>
        <List.Item>
          服務：{c.serviceName}（{c.active ? '啟用中' : '停用'}）
        </List.Item>
        <List.Item>
          單場時長：{c.durationMinMinutes} – {c.durationMaxMinutes} 分鐘
        </List.Item>
        <List.Item>
          人數：{c.minPartySize} – {c.maxPartySize} 人
        </List.Item>
        <List.Item>
          最短訂位提前：
          {c.leadTimeMinutes === 0 ? '無需提前' : `${c.leadTimeMinutes} 分鐘`}
        </List.Item>
        {c.blackoutWindows.length > 0 && (
          <List.Item>禁用時段：共 {c.blackoutWindows.length} 段</List.Item>
        )}
      </List>
    </Alert>
  );
}
